import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import GlobalFooter from './components/GlobalFooter';

// CSS for blob animations
const blobStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob {
    animation: blob 7s infinite;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }
`;

const AboutUs = () => {
  const navigate = useNavigate();
  const [expandedAdvisor, setExpandedAdvisor] = useState(null);

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Inject blob animation styles */}
      <style>{blobStyles}</style>

      {/* Hero Section - Enhanced */}
      <section className="relative py-24 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-blue-50 to-purple-50"></div>
        
        {/* Decorative floating shapes */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 -translate-x-1/2 -translate-y-1/2 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 translate-x-1/3 -translate-y-1/3 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 -translate-x-1/2 translate-y-1/2 animate-blob animation-delay-4000"></div>
        
        {/* Decorative circles */}
        <div className="absolute top-20 left-10 w-20 h-20 border-4 border-green-200 rounded-full opacity-50"></div>
        <div className="absolute top-40 right-20 w-12 h-12 bg-orange-200 rounded-lg rotate-45 opacity-50"></div>
        <div className="absolute bottom-20 left-1/4 w-8 h-8 bg-blue-300 rounded-full opacity-50"></div>
        
        <div className="container mx-auto px-4 max-w-4xl text-center relative z-10">
          <div className="inline-block mb-6">
            <span className="bg-green-100 text-green-700 text-sm font-semibold px-4 py-2 rounded-full">
              🌱 Our Story
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-blue-600">Food Connect</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            Reducing food waste. Increasing access. 
            <span className="font-semibold text-green-600"> Connecting compassion with logistics.</span>
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-lg px-10 py-6 rounded-full shadow-xl shadow-green-500/25 transition-all duration-300 hover:scale-105"
          >
            Partner with Us →
          </Button>
        </div>
      </section>

      {/* Wave Divider */}
      <div className="relative">
        <svg className="w-full h-16 -mb-1" viewBox="0 0 1440 64" fill="none" preserveAspectRatio="none">
          <path d="M0 64L60 53.3C120 43 240 21 360 16C480 11 600 21 720 26.7C840 32 960 32 1080 24C1200 16 1320 0 1380 -8L1440 -16V64H1380C1320 64 1200 64 1080 64C960 64 840 64 720 64C600 64 480 64 360 64C240 64 120 64 60 64H0Z" fill="#f0fdf4"/>
        </svg>
      </div>

      {/* Founder's Story - Enhanced */}
      <section className="py-24 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50"></div>
        
        {/* Animated floating blobs */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
        <div className="absolute top-40 -right-20 w-80 h-80 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-20 left-1/3 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 border-2 border-green-300 rounded-full opacity-40"></div>
        <div className="absolute bottom-32 left-16 w-20 h-20 border-2 border-teal-300 rounded-full opacity-40"></div>
        <div className="absolute top-1/2 right-10 w-16 h-16 bg-green-200 rounded-lg rotate-45 opacity-30"></div>
        
        {/* Dot pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle, #065f46 1px, transparent 1px)', backgroundSize: '24px 24px'}}></div>
        
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Founder's <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-teal-500">Story</span>
            </h2>
            <p className="text-gray-600 text-lg">The moment that changed everything</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <div className="bg-white/70 backdrop-blur-md rounded-3xl shadow-xl p-8 md:p-10 border border-white/50">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <span className="text-white text-2xl">💡</span>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The idea for Food Connect began one monsoon evening in Mumbai. Riya was in a car after dinner when she saw the restaurant worker tossing trays of untouched biryani and bread into a garbage bin. Just beyond the window, in the rain, a group of migrant laborers huddled under a plastic sheet, passing around a plate of plain dal and roti.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The contrast, <span className="font-semibold text-green-600">abundance and hunger separated by only a few feet</span>, stayed with her. It wasn't just about waste; it was about a broken system. Later, seeing the same divide in Singapore, she realized the problem wasn't scarcity, but access.
                </p>
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-gray-600 italic">
                    "That night became my turning point, the moment I decided to build Food Connect, to ensure empathy moves as swiftly as innovation."
                  </p>
                </div>
              </div>
            </div>
            
            {/* Founder Image */}
            <div className="order-1 lg:order-2 text-center">
              <div className="relative inline-block">
                {/* Decorative rings */}
                <div className="absolute inset-0 w-80 h-80 border-4 border-green-300 rounded-full -translate-x-4 -translate-y-4 opacity-60 animate-pulse"></div>
                <div className="absolute inset-0 w-80 h-80 border-4 border-teal-300 rounded-full translate-x-4 translate-y-4 opacity-60"></div>
                
                <div className="relative w-72 h-72 md:w-80 md:h-80 mx-auto bg-gradient-to-br from-green-100 to-teal-100 rounded-full flex items-center justify-center shadow-2xl overflow-hidden border-4 border-white">
                  <img 
                    src="/founder.png" 
                    alt="Riya Kamat - Founder & CEO" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="mt-8">
                <h3 className="text-2xl font-bold text-gray-800">Riya Kamat</h3>
                <p className="text-green-600 font-medium text-lg">Founder & CEO</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave Divider between sections */}
      <div className="relative bg-gradient-to-r from-green-50 to-teal-50">
        <svg className="w-full h-20 -mb-1" viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none">
          <path d="M0 80L48 68.3C96 57 192 33 288 26.7C384 20 480 30 576 36.7C672 43 768 47 864 43.3C960 40 1056 30 1152 30C1248 30 1344 40 1392 45L1440 50V80H1392C1344 80 1248 80 1152 80C1056 80 960 80 864 80C768 80 672 80 576 80C480 80 384 80 288 80C192 80 96 80 48 80H0Z" fill="#faf5ff"/>
        </svg>
      </div>

      {/* About the Founder - Enhanced */}
      <section className="py-24 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50"></div>
        
        {/* Animated floating blobs */}
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
        <div className="absolute top-1/2 -left-20 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-20 right-1/3 w-72 h-72 bg-fuchsia-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-32 left-20 w-24 h-24 border-2 border-purple-300 rounded-full opacity-40"></div>
        <div className="absolute bottom-20 right-16 w-16 h-16 border-2 border-pink-300 rounded-full opacity-40"></div>
        <div className="absolute top-20 right-1/4 w-12 h-12 bg-purple-200 rounded-lg rotate-45 opacity-40"></div>
        
        {/* Dot pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle, #7e22ce 1px, transparent 1px)', backgroundSize: '24px 24px'}}></div>
        
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              About the <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-pink-500">Founder</span>
            </h2>
            <p className="text-gray-600 text-lg">Innovation meets inclusion</p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {/* Centered Text Box */}
            <div className="bg-white/70 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/50">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Riya Kamat is the founder of Food Connect and a junior at Singapore American School who studies how systems function and where they fall short. Fascinated by the intersection of <span className="font-semibold text-purple-600">innovation and inclusion</span>, she examines inefficiencies in everyday structures and creates practical, tech-enabled solutions to close those gaps.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Through her research and internships at media-tech platforms like Animeta and Slurrp, she has explored how well-intentioned innovations can unintentionally exclude the very people they aim to serve.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                A champion powerlifter and recreational pilot-in-training, Riya approaches every pursuit with the same belief that drives Food Connect: <span className="font-semibold text-green-600">that progress means little unless it brings everyone to the table.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wave Divider */}
      <div className="relative bg-gradient-to-r from-purple-50 to-pink-50">
        <svg className="w-full h-20 -mb-1" viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none">
          <path d="M0 80L48 73.3C96 67 192 53 288 46.7C384 40 480 40 576 43.3C672 47 768 53 864 53.3C960 53 1056 47 1152 43.3C1248 40 1344 40 1392 40L1440 40V80H1392C1344 80 1248 80 1152 80C1056 80 960 80 864 80C768 80 672 80 576 80C480 80 384 80 288 80C192 80 96 80 48 80H0Z" fill="#ecfdf5"/>
        </svg>
      </div>

      {/* Advisors Section - Enhanced Background */}
      <section className="py-20 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50"></div>
        
        {/* Animated floating blobs */}
        <div className="absolute -top-20 left-1/4 w-80 h-80 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 -left-20 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-1/4 w-20 h-20 border-2 border-green-300 rounded-full opacity-40"></div>
        <div className="absolute bottom-32 left-20 w-16 h-16 border-2 border-teal-300 rounded-full opacity-40"></div>
        
        {/* Dot pattern overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'radial-gradient(circle, #065f46 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
        
        <div className="container mx-auto px-4 max-w-4xl relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-teal-500">Advisors</span>
          </h2>
          
          <div className="space-y-4">
            {/* Advisor 1 - Sanmita K. */}
            <div 
              className={`bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer transition-all duration-300 ${expandedAdvisor === 'sanmita' ? 'ring-2 ring-green-500' : 'hover:shadow-xl'}`}
              onClick={() => setExpandedAdvisor(expandedAdvisor === 'sanmita' ? null : 'sanmita')}
            >
              <div className="p-6 flex items-center gap-6">
                <div className="w-20 h-20 flex-shrink-0 bg-gray-200 rounded-full border-4 border-green-500 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_partner-pages-api/artifacts/iyksa7m8_Sanmita%20K%20updated.jpg" 
                    alt="Sanmita K."
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Sanmita K.</h3>
                  <p className="text-green-600 font-medium text-sm">Advisor, NGO & Community Partnerships</p>
                </div>
                <div className={`text-2xl text-gray-400 transition-transform duration-300 ${expandedAdvisor === 'sanmita' ? 'rotate-180' : ''}`}>
                  ▼
                </div>
              </div>
              
              <div className={`overflow-hidden transition-all duration-300 ${expandedAdvisor === 'sanmita' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Sanmita K. brings extensive experience working across CSR initiatives, youth-development programs, and NGO collaborations in India. She has partnered with organisations such as CRY and has led multiple community-engagement projects that support education, nutrition, and vulnerable families.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    At Food Connect, Sanmita advises on NGO partnerships and community outreach, helping shape programmes that are inclusive, responsible, and grounded in real on-the-ground needs. She guides partner selection, ensures alignment with community priorities, and strengthens Food Connect's ability to deliver consistent and meaningful social impact.
                  </p>
                </div>
              </div>
            </div>

            {/* Advisor 2 - Hitesh Gawankar */}
            <div 
              className={`bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer transition-all duration-300 ${expandedAdvisor === 'hitesh' ? 'ring-2 ring-green-500' : 'hover:shadow-xl'}`}
              onClick={() => setExpandedAdvisor(expandedAdvisor === 'hitesh' ? null : 'hitesh')}
            >
              <div className="p-6 flex items-center gap-6">
                <div className="w-20 h-20 flex-shrink-0 bg-gray-200 rounded-full border-4 border-green-500 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_partner-pages-api/artifacts/knfvmrln_Hitesh%20Gawankar%20updated.jpg" 
                    alt="Hitesh Gawankar"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Hitesh Gawankar</h3>
                  <p className="text-green-600 font-medium text-sm">Advisor, Technology</p>
                </div>
                <div className={`text-2xl text-gray-400 transition-transform duration-300 ${expandedAdvisor === 'hitesh' ? 'rotate-180' : ''}`}>
                  ▼
                </div>
              </div>
              
              <div className={`overflow-hidden transition-all duration-300 ${expandedAdvisor === 'hitesh' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Hitesh is the founder of Digital Dreamwork and brings a strong track record in process optimization, creative-tech projects, and building digital systems. He is experienced in applying technology and digital tools to streamline workflows - from storytelling and content generation to system design and execution.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    For Food Connect, Hitesh leads technology strategy: helping design and implement platform, data-management, logistics and supply-chain systems - ensuring the project benefits from scalable, efficient and tech-driven operations.
                  </p>
                </div>
              </div>
            </div>

            {/* Advisor 3 - Gaurav Mishra */}
            <div 
              className={`bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer transition-all duration-300 ${expandedAdvisor === 'gaurav' ? 'ring-2 ring-green-500' : 'hover:shadow-xl'}`}
              onClick={() => setExpandedAdvisor(expandedAdvisor === 'gaurav' ? null : 'gaurav')}
            >
              <div className="p-6 flex items-center gap-6">
                <div className="w-20 h-20 flex-shrink-0 bg-gray-200 rounded-full border-4 border-green-500 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_partner-pages-api/artifacts/z6vn4b6g_Gaurav%20Mishra%20updated.jpg" 
                    alt="Gaurav Mishra"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Gaurav Mishra</h3>
                  <p className="text-green-600 font-medium text-sm">Advisor, Food Sourcing & Operations</p>
                </div>
                <div className={`text-2xl text-gray-400 transition-transform duration-300 ${expandedAdvisor === 'gaurav' ? 'rotate-180' : ''}`}>
                  ▼
                </div>
              </div>
              
              <div className={`overflow-hidden transition-all duration-300 ${expandedAdvisor === 'gaurav' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Gaurav Mishra is the owner of Colonial Ventures and operates several F&B brands in Singapore, including Salt & Pepper, Red Chillis, and Elite Eats. He brings hands-on expertise in restaurant operations, food sourcing, kitchen workflows, and large-scale catering management.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    At Food Connect, Gaurav advises on supply-chain logistics, vendor selection, and quality control, ensuring that every meal delivered through the programme is safe, consistent, and operationally reliable.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where We Work & Stats - Enhanced */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-green-900 to-gray-900"></div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Where We Work */}
            <div className="text-center lg:text-left">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Where We <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">Work</span>
              </h2>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-6">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-4 flex items-center gap-3">
                  <span className="text-3xl">🇮🇳</span>
                  <div>
                    <div className="text-white font-semibold">India</div>
                    <div className="text-gray-400 text-sm">Mumbai • Bangalore</div>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-4 flex items-center gap-3">
                  <span className="text-3xl">🇸🇬</span>
                  <div>
                    <div className="text-white font-semibold">Singapore</div>
                    <div className="text-gray-400 text-sm">Island-wide</div>
                  </div>
                </div>
              </div>
              <p className="text-gray-400 text-lg">
                Starting local, scaling responsibly.
              </p>
            </div>

            {/* Stats */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 text-center lg:text-left">
                Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">Impact</span>
              </h2>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: "1,500+", label: "Meals Rescued", icon: "🍽️" },
                  { value: "41+", label: "Partners", icon: "🤝" },
                  { value: "150+", label: "Volunteers", icon: "👥" },
                ].map((stat, index) => (
                  <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-all duration-300 hover:-translate-y-1">
                    <div className="text-2xl mb-2">{stat.icon}</div>
                    <div className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-gray-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner - Enhanced */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-green-600 to-teal-500"></div>
        
        {/* Decorative shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-white/10 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/10 rounded-full"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join the Mission
          </h2>
          <p className="text-xl text-green-100 mb-10 max-w-2xl mx-auto">
            Be part of the solution. Whether you're a restaurant, NGO, or individual volunteer, 
            there's a place for you in our network.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6 rounded-full shadow-xl transition-all duration-300 hover:scale-105 font-semibold"
            >
              Partner Restaurant
            </Button>
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white/20 text-white hover:bg-white/30 text-lg px-8 py-6 rounded-full border-2 border-white/50 transition-all duration-300 hover:scale-105 font-semibold"
            >
              NGO Partner
            </Button>
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white/20 text-white hover:bg-white/30 text-lg px-8 py-6 rounded-full border-2 border-white/50 transition-all duration-300 hover:scale-105 font-semibold"
            >
              Volunteer
            </Button>
          </div>
        </div>
      </section>

      {/* Global Footer */}
      <GlobalFooter />
    </div>
  );
};

export default AboutUs;
